import { createPopper } from '@popperjs/core'
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

/**
 * Composable genérico para inputs com dropdown de busca
 * @param {Object} options - Configurações
 * @param {Function} options.fetchService - Serviço para buscar dados (ex: Clients.list)
 * @param {Function} options.readService - Serviço para ler item único (ex: Clients.read)
 * @param {Function} options.extractItem - Função customizada para extrair item do response (opcional)
 * @param {Function} options.buildSearchUrl - Função que constrói a URL de busca
 * @param {Function} options.onSelect - Callback quando um item é selecionado
 * @param {Function} options.onNoResults - Callback quando não há resultados (opcional)
 * @param {Number} options.debounceDelay - Delay do debounce em ms (default: 800)
 * @param {Ref} options.inputRef - Ref do elemento input
 * @param {Ref} options.dropdownRef - Ref do elemento dropdown
 * @param {Ref} options.ulRef - Ref do elemento ul (opcional, para scroll)
 * @param {Ref} options.initialValue - Valor inicial para carregar (opcional)
 * @param {String} options.valueType - Tipo do valor ('id' ou 'document', default: 'id')
 */
export function useSearchDropdown(options) {
  const {
    fetchService,
    readService,
    extractItem,
    buildSearchUrl,
    onSelect,
    onNoResults,
    debounceDelay = 800,
    inputRef,
    dropdownRef,
    ulRef,
    initialValue,
  } = options

  // State
  const searchQuery = ref('')
  const results = ref([])
  const isDropdownVisible = ref(false)
  const isLoading = ref(false)
  const isFinished = ref(false)
  const url = ref('')
  const debounceTimeout = ref(null)
  const popperInstance = ref(null)
  const activeIndex = ref(-1)
  const isMouseOverDropdown = ref(false)
  const isKeyboardNavigation = ref(false)
  const isSelecting = ref(false)
  const isInitializing = ref(true)
  const isClearing = ref(false)

  // Debounce helper
  const debounce = (func, delay) => {
    clearTimeout(debounceTimeout.value)
    debounceTimeout.value = setTimeout(func, delay)
  }

  // Reset URL e resultados
  const resetUrl = () => {
    isFinished.value = false
    results.value = []
    activeIndex.value = -1
    url.value = buildSearchUrl(searchQuery.value)
  }

  /**
   * Busca resultados da API com suporte a paginação infinita
   * - Previne buscas duplicadas quando já está carregando
   * - Adiciona resultados ao array existente (não substitui)
   * - Define primeiro item como ativo na primeira carga
   * - Chama onNoResults quando busca retorna vazio
   */
  const fetchResults = async () => {
    if (isLoading.value || isFinished.value) return

    isLoading.value = true

    try {
      const response = await fetchService(url.value)

      const isFirstLoad = results.value.length === 0
      results.value.push(...response.data.results)

      // Define o primeiro item como ativo na primeira carga
      if (
        isFirstLoad &&
        results.value.length > 0 &&
        isDropdownVisible.value &&
        !isMouseOverDropdown.value
      ) {
        activeIndex.value = 0
      }

      if (response.data.next) {
        url.value = `?${response.data.next.split('?')[1]}`
      } else {
        url.value = null
        isFinished.value = true

        // Callback quando não há resultados
        if (results.value.length === 0 && onNoResults) {
          onNoResults()
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Seleciona um item do dropdown
   * - Marca isSelecting=true para prevenir watch de searchQuery
   * - Executa callback onSelect (atualiza v-model, etc)
   * - Fecha dropdown e limpa activeIndex
   * @param {Object} item - Item selecionado da lista
   */
  const selectResult = item => {
    isSelecting.value = true
    onSelect(item)
    isDropdownVisible.value = false
    activeIndex.value = -1

    nextTick(() => {
      isSelecting.value = false
    })
  }

  // Abrir dropdown
  const openDropdown = () => {
    isDropdownVisible.value = true
    if (results.value.length > 0 && !isMouseOverDropdown.value) {
      activeIndex.value = 0
    }
  }

  // Fechar dropdown
  const closeDropdown = () => {
    isDropdownVisible.value = false
    activeIndex.value = -1
    isMouseOverDropdown.value = false
    isKeyboardNavigation.value = false
  }

  // Limpar tudo
  const cleanAll = () => {
    isClearing.value = true
    searchQuery.value = ''
    if (inputRef.value) {
      inputRef.value.focus()
    }
    nextTick(() => {
      resetUrl()
      fetchResults().then(() => {
        isDropdownVisible.value = true
        if (results.value.length > 0 && !isMouseOverDropdown.value) {
          activeIndex.value = 0
        }
        isClearing.value = false
      })
    })
  }

  /**
   * Handler de navegação por teclado no dropdown
   * - ArrowDown/ArrowUp: Navega entre itens com scroll automático
   * - Enter: Seleciona item ativo e remove foco do input
   * - Escape: Fecha dropdown
   * @param {KeyboardEvent} event - Evento de teclado
   */
  const handleKeyDown = event => {
    if (!isDropdownVisible.value) return

    switch (event.key) {
      case 'ArrowDown':
        isKeyboardNavigation.value = true
        activeIndex.value = (activeIndex.value + 1) % results.value.length
        scrollToActiveItem()
        break
      case 'ArrowUp':
        isKeyboardNavigation.value = true
        activeIndex.value = (activeIndex.value - 1 + results.value.length) % results.value.length
        scrollToActiveItem()
        break
      case 'Enter':
        event.preventDefault()
        event.stopPropagation()

        if (isLoading.value || results.value.length === 0) return

        const indexToSelect = activeIndex.value >= 0 ? activeIndex.value : 0
        if (indexToSelect < results.value.length) {
          selectResult(results.value[indexToSelect])
          if (inputRef.value) {
            inputRef.value.blur()
          }
        }
        break
      case 'Escape':
        closeDropdown()
        break
    }
  }

  /**
   * Handler de scroll infinito para paginação
   * Quando usuário rola próximo ao final (100px), carrega próxima página
   * Requer ulRef para funcionar corretamente
   */
  const handleScroll = () => {
    const ul = ulRef?.value
    if (!ul) return

    const nearBottom = ul.scrollTop + ul.clientHeight >= ul.scrollHeight - 100
    if (nearBottom) {
      fetchResults()
    }
  }

  /**
   * Rola o dropdown para manter o item ativo visível
   * Usado durante navegação por teclado (ArrowUp/Down)
   * Calcula se item está acima ou abaixo da área visível
   */
  const scrollToActiveItem = () => {
    const ul = ulRef?.value
    if (!ul) return

    const items = ul.querySelectorAll('li')
    if (activeIndex.value < 0 || activeIndex.value >= items.length) return

    const activeItem = items[activeIndex.value]
    const ulTop = ul.scrollTop
    const ulBottom = ulTop + ul.clientHeight
    const itemTop = activeItem.offsetTop
    const itemBottom = itemTop + activeItem.clientHeight

    if (itemTop < ulTop) {
      ul.scrollTop = itemTop
    } else if (itemBottom > ulBottom) {
      ul.scrollTop = itemBottom - ul.clientHeight
    }
  }

  // Mouse handlers
  const handleMouseEnterDropdown = () => {
    isMouseOverDropdown.value = true
  }

  const handleMouseLeaveDropdown = () => {
    isMouseOverDropdown.value = false
    isKeyboardNavigation.value = false
    if (results.value.length > 0 && isDropdownVisible.value) {
      activeIndex.value = 0
    }
  }

  const handleMouseEnterItem = index => {
    isKeyboardNavigation.value = false
    activeIndex.value = index
  }

  /**
   * Inicializa o componente com um valor pré-existente
   * - Se há valor: busca o item via readService e seleciona
   * - Se não há valor: faz busca inicial vazia
   * - Usa extractItem para lidar com diferentes estruturas de resposta
   * @param {string|number} value - ID ou valor para buscar
   */
  const initializeWithValue = async value => {
    if (!value) {
      await fetchResults()
      isInitializing.value = false
      return
    }

    try {
      const resp = await readService(value)
      const item = extractItem ? extractItem(resp) : resp.data

      if (item) {
        results.value = [item]
        isSelecting.value = true
        onSelect(item)
        await nextTick()
        isSelecting.value = false
      }
    } finally {
      isInitializing.value = false
    }
  }

  /**
   * Watch que reage a mudanças no texto digitado
   * - Ignora durante seleção de item (isSelecting=true)
   * - Fecha dropdown e limpa resultados imediatamente
   * - Faz debounce antes de buscar (evita requisições desnecessárias)
   * - Reabre dropdown após carregar resultados
   * - Define primeiro item como ativo automaticamente
   */
  watch(searchQuery, (newValue, oldValue) => {
    if (isSelecting.value || isClearing.value) return

    // Quando digita, fecha dropdown e limpa resultados
    if (searchQuery.value && newValue !== oldValue) {
      isLoading.value = true
      isDropdownVisible.value = false
      results.value = []
      activeIndex.value = -1
      isFinished.value = false
    }

    // Busca com debounce
    debounce(() => {
      isLoading.value = false
      resetUrl()
      fetchResults().then(() => {
        if (searchQuery.value) {
          isDropdownVisible.value = true
          // Define o primeiro item como ativo após abrir o dropdown
          if (results.value.length > 0 && !isMouseOverDropdown.value) {
            activeIndex.value = 0
          }
        }
      })
    }, debounceDelay)
  })

  // Setup popper e inicialização
  onMounted(async () => {
    if (inputRef.value && dropdownRef.value) {
      popperInstance.value = createPopper(inputRef.value, dropdownRef.value, {
        placement: 'bottom',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              altBoundary: true,
            },
          },
        ],
      })
    }

    resetUrl()
    await nextTick()
    await initializeWithValue(initialValue?.value)
  })

  // Watch para reagir a mudanças no initialValue após montagem
  watch(
    () => initialValue?.value,
    async (newValue, oldValue) => {
      if (oldValue === undefined || newValue === oldValue || !newValue) return
      if (isInitializing.value || isSelecting.value) return

      // Seta flag ANTES para prevenir loop quando onSelect atualizar o v-model
      isSelecting.value = true
      await initializeWithValue(newValue)
    },
    { immediate: false },
  )

  // Cleanup
  onUnmounted(() => {
    clearTimeout(debounceTimeout.value)
    if (popperInstance.value) {
      popperInstance.value.destroy()
    }
  })

  return {
    // State
    searchQuery,
    results,
    isDropdownVisible,
    isLoading,
    isFinished,
    activeIndex,
    isMouseOverDropdown,
    isKeyboardNavigation,
    isInitializing,
    isSelecting,

    // Methods
    openDropdown,
    closeDropdown,
    selectResult,
    cleanAll,
    handleKeyDown,
    handleScroll,
    handleMouseEnterDropdown,
    handleMouseLeaveDropdown,
    handleMouseEnterItem,
    initializeWithValue,
    fetchResults,
    resetUrl,
  }
}
